/**
 * Shared ScreenControlPanel Component
 *
 * Top control panel for screen editor dialog.
 * Narrower panel with only Close button and screen name.
 * Save button has been moved to EditorToolbar.
 *
 * Features:
 * - Display screen name
 * - Close button
 *
 * Related: Sprint-6, FR-021 to FR-026, EditorToolbar
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Button, IconButton, TextField, useTheme } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  LAYOUT_CONTROL_PANEL_HEIGHT,
  SPACING,
  TYPO_LABEL_SM,
  TYPO_BODY,
} from '../../config/ui';

export interface ScreenControlPanelProps {
  /** Screen name to display */
  screenName: string;

  /** Screen type (e.g., 'ScreenImageTitleSubtitles') */
  screenType?: string;

  /** Screen ID */
  screenId?: string;

  /** Whether save operation is in progress (disables close) */
  isSaving?: boolean;

  /** Callback when Close is clicked */
  onClose: () => void;

  /** Callback when screen name is changed */
  onNameChange?: (newName: string) => void;

  /** Names of other screens in the onboarding (for uniqueness validation) */
  existingNames?: string[];

  /** Optional right-side content for contextual actions/help */
  endAdornment?: React.ReactNode;
}

/**
 * Control panel for screen editor
 *
 * Displays screen name and Close button.
 * Save functionality has been moved to EditorToolbar.
 */
export const ScreenControlPanel: React.FC<ScreenControlPanelProps> = ({
  screenName,
  screenType,
  screenId,
  isSaving = false,
  onClose,
  onNameChange,
  existingNames,
  endAdornment,
}) => {
  const theme = useTheme();

  // Inline editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(screenName);

  // Uniqueness validation (case-insensitive)
  const isDuplicate = useMemo(() => {
    if (!existingNames || !editValue.trim()) return false;
    const trimmed = editValue.trim().toLowerCase();
    return existingNames.some(name => name.toLowerCase() === trimmed);
  }, [editValue, existingNames]);

  // Ref for the input element — ensures reliable text selection on edit start
  const inputRef = useRef<HTMLInputElement>(null);

  // Select all text when entering edit mode (more reliable than autoFocus + onFocus)
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Sync editValue when screenName prop changes (and not currently editing)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(screenName);
    }
  }, [screenName, isEditing]);

  // Handlers for inline editing
  const handleEditClick = useCallback(() => {
    setEditValue(screenName);
    setIsEditing(true);
  }, [screenName]);

  const handleConfirm = useCallback(() => {
    if (isDuplicate) return;
    const trimmed = editValue.trim();
    if (trimmed && onNameChange) {
      onNameChange(trimmed);
    }
    setIsEditing(false);
  }, [editValue, onNameChange, isDuplicate]);

  const handleCancel = useCallback(() => {
    setEditValue(screenName);
    setIsEditing(false);
  }, [screenName]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isDuplicate) {
      e.preventDefault();
      handleConfirm();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
    // Ctrl+S / Cmd+S: confirm name, let event bubble to dialog's save handler
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      if (!isDuplicate) {
        handleConfirm();
      }
    }
  }, [handleConfirm, handleCancel, isDuplicate]);

  return (
    <Box
      data-testid="editor-control-panel"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: SPACING.md,
        padding: theme.spacing(SPACING.sm, SPACING.md),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        minHeight: LAYOUT_CONTROL_PANEL_HEIGHT,
        height: LAYOUT_CONTROL_PANEL_HEIGHT,
      }}
    >
      {/* Left: Close Button */}
      <Button
        data-testid="editor-close-btn"
        onClick={onClose}
        disabled={isSaving}
        variant="text"
        color="primary"
      >
        CLOSE
      </Button>

      {/* Screen Info - Type, Name, ID */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Screen Type */}
        {screenType && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: TYPO_LABEL_SM,
            }}
          >
            {screenType}
          </Typography>
        )}

        {/* Screen Name - with inline editing */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEditing ? (
            <>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: TYPO_BODY,
                  fontWeight: 500,
                }}
              >
                Screen Name:
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  size="small"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  inputRef={inputRef}
                  error={isDuplicate}
                  data-testid="screen-name-input"
                  sx={{
                    minWidth: 200,
                    '& .MuiInputBase-input': {
                      fontSize: TYPO_BODY,
                      padding: '4px 8px',
                    },
                  }}
                />
                {isDuplicate && (
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: '100%',
                      color: 'error.main',
                      fontSize: '10px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    This name already exists
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={handleConfirm}
                color="primary"
                disabled={isDuplicate || !editValue.trim()}
                data-testid="screen-name-confirm-button"
              >
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleCancel} data-testid="screen-name-cancel-button">
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          ) : (
            <>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: TYPO_BODY,
                  fontWeight: 500,
                }}
              >
                Screen Name: {screenName || 'Untitled'}
              </Typography>
              {onNameChange && (
                <IconButton size="small" onClick={handleEditClick} data-testid="screen-name-edit-button">
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </>
          )}
        </Box>

        {/* Screen ID */}
        {screenId && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: TYPO_LABEL_SM,
            }}
          >
            Screen ID: {screenId}
          </Typography>
        )}
      </Box>

      {endAdornment && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {endAdornment}
        </Box>
      )}
    </Box>
  );
};
